import { useEffect, useMemo, useRef } from 'react';

/** While a hand is held down on the canvas, how often the chrome catches up with the page, in ms. */
export const HELD_MS = 120;

interface Clock {
  now(): number;
  frame(run: () => void): number;
  cancelFrame(id: number): void;
  later(run: () => void, ms: number): number;
  cancelLater(id: number): void;
}

const BROWSER: Clock = {
  now: () => performance.now(),
  frame: (run) => requestAnimationFrame(run),
  cancelFrame: (id) => cancelAnimationFrame(id),
  later: (run, ms) => window.setTimeout(run, ms),
  cancelLater: (id) => window.clearTimeout(id),
};

/**
 * Tells the chrome the page changed without telling it sixty times a second.
 *
 * Every change asks for one refresh. Unheld, the asks of one frame become one refresh on
 * the next frame. Held — a drag on the canvas, which writes the page on every move — at
 * most one every `HELD_MS`, and on letting go whatever is still owed is shown at once.
 */
export class PanelRefresh {
  private frameId = 0;
  private timerId = 0;
  private last = Number.NEGATIVE_INFINITY;
  private owed = false;
  held = false;

  constructor(private readonly refresh: () => void, private readonly clock: Clock = BROWSER) {}

  schedule(): void {
    this.owed = true;
    if (this.frameId || this.timerId) return;
    if (this.held) this.timerId = this.clock.later(this.fire, Math.max(0, HELD_MS - (this.clock.now() - this.last)));
    else this.frameId = this.clock.frame(this.fire);
  }

  hold(on: boolean): void {
    this.held = on;
    // Let go: what the drag still owes the panel is shown now, not a beat later.
    if (!on && this.timerId) {
      this.clock.cancelLater(this.timerId);
      this.timerId = 0;
      this.fire();
    }
  }

  dispose(): void {
    if (this.frameId) this.clock.cancelFrame(this.frameId);
    if (this.timerId) this.clock.cancelLater(this.timerId);
    this.frameId = this.timerId = 0;
    this.owed = false;
  }

  private fire = (): void => {
    this.frameId = this.timerId = 0;
    if (!this.owed) return;
    this.owed = false;
    this.last = this.clock.now();
    this.refresh();
  };
}

/**
 * One `PanelRefresh` for the editor, held while a pointer is down on a canvas. Only the
 * canvas: a slider dragged in the side panel is a hand held too, and that panel has to
 * keep up with its own slider.
 */
export function usePanelRefresh(refresh: () => void): PanelRefresh {
  const latest = useRef(refresh);
  latest.current = refresh;
  const panel = useMemo(() => new PanelRefresh(() => latest.current()), []);
  useEffect(() => {
    const down = (event: PointerEvent) => {
      if (event.target instanceof HTMLCanvasElement) panel.hold(true);
    };
    const up = () => panel.hold(false);
    window.addEventListener('pointerdown', down, true);
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', up, true);
    window.addEventListener('blur', up);
    return () => {
      window.removeEventListener('pointerdown', down, true);
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('pointercancel', up, true);
      window.removeEventListener('blur', up);
      panel.dispose();
    };
  }, [panel]);
  return panel;
}
